import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Clock, Loader2, Plus, Pencil, Trash2, Mail, Phone, MessageSquare, Copy, ExternalLink } from "lucide-react";
import UserManagement from "@/components/UserManagement";
import { Switch } from "@/components/ui/switch";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPERTY_TYPES = ["Appartamento", "Villa", "Monolocale", "Bilocale", "Trilocale", "Attico", "Loft", "Casa indipendente"];

const TEMPLATE_TYPE_CONFIG = {
  email: { label: "Email", icon: Mail, color: "bg-blue-100 text-blue-700" },
  portal_message: { label: "Messaggio Portale", icon: MessageSquare, color: "bg-violet-100 text-violet-700" },
  phone_script: { label: "Script Telefonata", icon: Phone, color: "bg-amber-100 text-amber-700" },
};

function TemplateEditor({ template, portals, onSave, onClose }) {
  const [form, setForm] = useState({
    name: template?.name || "",
    portal_name: template?.portal_name || "_default",
    template_type: template?.template_type || "email",
    subject: template?.subject || "",
    body: template?.body || "",
    active: template?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.body) {
      toast.error("Nome e corpo del messaggio sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Nome template</Label>
          <Input
            data-testid="template-name-input"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="es. Primo contatto email"
            className="h-9 rounded-sm border-zinc-300"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Portale</Label>
          <Select value={form.portal_name} onValueChange={(v) => setForm(prev => ({ ...prev, portal_name: v }))}>
            <SelectTrigger data-testid="template-portal-select" className="h-9 rounded-sm border-zinc-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_default">Tutti i portali (default)</SelectItem>
              {portals.map(p => (
                <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Tipo</Label>
        <Select value={form.template_type} onValueChange={(v) => setForm(prev => ({ ...prev, template_type: v }))}>
          <SelectTrigger data-testid="template-type-select" className="h-9 rounded-sm border-zinc-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="portal_message">Messaggio Portale</SelectItem>
            <SelectItem value="phone_script">Script Telefonata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {form.template_type !== "phone_script" && (
        <div className="space-y-1.5">
          <Label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Oggetto</Label>
          <Input
            data-testid="template-subject-input"
            value={form.subject}
            onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="es. Interesse per {property_title}"
            className="h-9 rounded-sm border-zinc-300"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Corpo messaggio</Label>
        <Textarea
          data-testid="template-body-input"
          value={form.body}
          onChange={(e) => setForm(prev => ({ ...prev, body: e.target.value }))}
          rows={8}
          placeholder="Scrivi il tuo template..."
          className="rounded-sm border-zinc-300 text-sm"
        />
      </div>

      <div className="p-3 bg-zinc-50 rounded-sm">
        <p className="text-xs text-zinc-500 font-semibold mb-1.5">Variabili disponibili:</p>
        <div className="flex flex-wrap gap-1.5">
          {["{property_title}", "{zone}", "{price}", "{rooms}", "{sqm}", "{portal_name}", "{property_type}"].map(v => (
            <button
              key={v}
              onClick={() => setForm(prev => ({ ...prev, body: prev.body + " " + v }))}
              className="px-2 py-0.5 bg-white border border-zinc-200 rounded-sm text-xs font-mono text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose} className="rounded-sm h-9">
          Annulla
        </Button>
        <Button
          data-testid="save-template-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Salva
        </Button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [criteria, setCriteria] = useState({
    zone: "", price_min: 0, price_max: 5000000, property_type: "", rooms_min: 0, sqm_min: 0
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchLogs, setSearchLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [portals, setPortals] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [fbGroups, setFbGroups] = useState([]);
  const [newFbUrl, setNewFbUrl] = useState("");
  const [newFbName, setNewFbName] = useState("");
  const [fbCookieStatus, setFbCookieStatus] = useState(null);
  const [fbCookieForm, setFbCookieForm] = useState({ c_user: "", xs: "" });
  const [fbVerifying, setFbVerifying] = useState(false);
  const [fbSaving, setFbSaving] = useState(false);
  const [apiCredits, setApiCredits] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [criteriaRes, logsRes, templatesRes, portalsRes, fbRes, fbCookieRes, creditsRes] = await Promise.all([
          axios.get(`${API}/search/criteria`),
          axios.get(`${API}/search/logs`),
          axios.get(`${API}/templates`),
          axios.get(`${API}/portals`),
          axios.get(`${API}/fb-groups`),
          axios.get(`${API}/fb-cookies/status`),
          axios.get(`${API}/scraping/credits`),
          axios.get(`${API}/fb-cookies/status`),
        ]);
        setCriteria(criteriaRes.data);
        setSearchLogs(logsRes.data);
        setTemplates(templatesRes.data);
        setPortals(portalsRes.data);
        setFbGroups(fbRes.data);
        setFbCookieStatus(fbCookieRes.data);
        setApiCredits(creditsRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, []);

  const saveCriteria = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/search/criteria`, criteria);
      toast.success("Criteri di ricerca salvati");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
    setSaving(false);
  };

  const saveTemplate = async (formData) => {
    if (editingTemplate) {
      await axios.put(`${API}/templates/${editingTemplate.id}`, formData);
      toast.success("Template aggiornato");
    } else {
      await axios.post(`${API}/templates`, formData);
      toast.success("Template creato");
    }
    const res = await axios.get(`${API}/templates`);
    setTemplates(res.data);
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success("Template eliminato");
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const copyTemplateBody = (template) => {
    navigator.clipboard.writeText(template.body);
    toast.success("Template copiato negli appunti");
  };

  const addFbGroup = async () => {
    if (!newFbUrl.trim()) { toast.error("Inserisci URL del gruppo"); return; }
    try {
      const res = await axios.post(`${API}/fb-groups`, { name: newFbName.trim() || newFbUrl.split("/groups/").pop()?.split("?")[0] || "Nuovo gruppo", url: newFbUrl.trim() });
      setFbGroups(prev => [...prev, res.data]);
      setNewFbUrl("");
      setNewFbName("");
      toast.success("Gruppo aggiunto");
    } catch (e) { toast.error("Errore"); }
  };

  const deleteFbGroup = async (id) => {
    try {
      await axios.delete(`${API}/fb-groups/${id}`);
      setFbGroups(prev => prev.filter(g => g.id !== id));
      toast.success("Gruppo rimosso");
    } catch (e) { toast.error("Errore"); }
  };

  const toggleFbGroup = async (id) => {
    try {
      const res = await axios.put(`${API}/fb-groups/${id}/toggle`);
      setFbGroups(prev => prev.map(g => g.id === id ? { ...g, active: res.data.active } : g));
    } catch (e) { toast.error("Errore"); }
  };

  const [cronEnabled, setCronEnabled] = useState(true);
  const [cronLoading, setCronLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/cron`).then(r => setCronEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const toggleCron = async () => {
    setCronLoading(true);
    try {
      const res = await axios.post(`${API}/settings/cron`);
      setCronEnabled(res.data.enabled);
      toast.success(res.data.enabled ? "Ricerca automatica attivata" : "Ricerca automatica disattivata");
    } catch { toast.error("Errore"); }
    setCronLoading(false);
  };

  const runCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await axios.post(`${API}/search/cleanup-zones`);
      setCleanupResult(res.data);
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore pulizia"); }
    setCleanupLoading(false);
  };

  const runRebalance = async () => {
    setRebalanceLoading(true);
    try {
      const res = await axios.post(`${API}/search/rebalance`);
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ribilanciamento"); }
    setRebalanceLoading(false);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="p-4 sm:p-6 space-y-6 sm:space-y-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">
          Impostazioni
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Configura criteri di ricerca, template contatto e preferenze dell'agente.
        </p>
      </div>

      {/* Ricerca Automatica */}
      <div className={`border rounded-sm p-4 sm:p-6 ${cronEnabled ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30"}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
              Ricerca Automatica
            </h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              {cronEnabled ? "La ricerca si avvia ogni 2 giorni alle 07:00" : "La ricerca automatica è disattivata"}
            </p>
          </div>
          <button
            data-testid="settings-cron-toggle"
            onClick={toggleCron}
            disabled={cronLoading}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-bold border transition-all ${
              cronEnabled
                ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                : "bg-red-600 border-red-600 text-white hover:bg-red-700"
            }`}
          >
            {cronLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {cronEnabled ? "Attiva — Disattiva" : "Disattivata — Riattiva"}
          </button>
        </div>
      </div>

      {/* Manutenzione Database */}
      <div className="border border-zinc-200 rounded-sm p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
          Manutenzione
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-zinc-100 rounded-sm p-4">
            <div className="text-sm font-semibold text-zinc-800">Pulizia Zone</div>
            <p className="text-xs text-zinc-400 mt-0.5 mb-3">Elimina gli immobili residenziali fuori dai comuni configurati</p>
            <button
              data-testid="cleanup-zones-btn"
              onClick={runCleanup}
              disabled={cleanupLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
            >
              {cleanupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Lancia Pulizia
            </button>
            {cleanupResult && (
              <div className="mt-2 text-xs text-zinc-500">
                <p className="font-semibold">{cleanupResult.message}</p>
                {cleanupResult.removed?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {cleanupResult.removed.map((r, i) => (
                      <li key={i} className="text-red-500">{r.zone} — {r.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="border border-zinc-100 rounded-sm p-4">
            <div className="text-sm font-semibold text-zinc-800">Ribilancia Assegnazioni</div>
            <p className="text-xs text-zinc-400 mt-0.5 mb-3">Redistribuisce gli immobili equamente tra gli agenti</p>
            <button
              data-testid="rebalance-btn"
              onClick={runRebalance}
              disabled={rebalanceLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              {rebalanceLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Ribilancia
            </button>
          </div>
        </div>
      </div>

      {/* API Credits */}
      {apiCredits?.proxies && (
        <div className="border border-zinc-200 rounded-sm p-4 sm:p-6 space-y-4">
          <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
            Crediti API Scraping
          </h2>
          <p className="text-xs text-zinc-400">{apiCredits.completed_searches} ricerche completate su {apiCredits.total_searches} totali</p>
          <div className="space-y-3">
            {Object.entries(apiCredits.proxies).map(([name, info]) => {
              const pct = info.remaining_percent ?? null;
              const hasError = !!info.error;
              return (
                <div key={name} className="border border-zinc-100 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-bold text-zinc-800">{name}</span>
                    {hasError ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-sm bg-red-50 text-red-600">Errore</span>
                    ) : pct != null ? (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${
                        pct > 50 ? "bg-emerald-50 text-emerald-700" : pct > 20 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"
                      }`}>
                        {pct}% disponibile
                      </span>
                    ) : (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">Attivo</span>
                    )}
                  </div>
                  {pct != null && !hasError ? (
                    <div className="w-full bg-zinc-200 rounded-full h-2.5 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${
                        pct > 50 ? "bg-emerald-500" : pct > 20 ? "bg-amber-500" : "bg-red-500"
                      }`} style={{ width: `${pct}%` }} />
                    </div>
                  ) : hasError ? (
                    <p className="text-[10px] text-red-400">{info.error}</p>
                  ) : null}
                  {info.remaining != null && !hasError && (
                    <p className="text-[10px] text-zinc-400 mt-1">
                      {info.remaining?.toLocaleString("it-IT")} richieste rimanenti
                      {info.max ? ` su ${info.max.toLocaleString("it-IT")}` : ""}
                    </p>
                  )}
                  {info.period_ends_at && (
                    <p className="text-[10px] text-zinc-400 mt-0.5">Rinnovo: {new Date(info.period_ends_at).toLocaleDateString("it-IT")}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contact Templates */}
      <div className="border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
            Template contatto
          </h2>
          <Dialog open={showEditor} onOpenChange={setShowEditor}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-template-btn"
                onClick={() => { setEditingTemplate(null); setShowEditor(true); }}
                className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-8 px-3 text-xs font-semibold"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg font-bold">
                  {editingTemplate ? "Modifica template" : "Nuovo template"}
                </DialogTitle>
              </DialogHeader>
              <TemplateEditor
                template={editingTemplate}
                portals={portals}
                onSave={saveTemplate}
                onClose={() => setShowEditor(false)}
              />
            </DialogContent>
          </Dialog>
        </div>

        <p className="text-sm text-zinc-500">
          Crea template personalizzati per contattare i venditori. Usa le variabili per auto-compilare i dati dell'immobile.
        </p>

        <div className="space-y-2">
          {templates.map(tpl => {
            const typeConfig = TEMPLATE_TYPE_CONFIG[tpl.template_type] || TEMPLATE_TYPE_CONFIG.email;
            const TypeIcon = typeConfig.icon;
            return (
              <div
                key={tpl.id}
                data-testid={`template-card-${tpl.id}`}
                className="border border-zinc-200 rounded-sm p-4 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-sm bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5">
                      <TypeIcon className="w-4 h-4 text-zinc-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-sm font-bold text-zinc-900">{tpl.name}</h3>
                        <Badge className={`${typeConfig.color} text-[10px] rounded-sm px-1.5 py-0 border-0`}>
                          {typeConfig.label}
                        </Badge>
                        {tpl.portal_name !== "_default" && (
                          <Badge className="bg-zinc-900 text-white text-[10px] rounded-sm px-1.5 py-0 border-0">
                            {tpl.portal_name}
                          </Badge>
                        )}
                      </div>
                      {tpl.subject && (
                        <p className="text-xs text-zinc-500 mt-0.5">Oggetto: {tpl.subject}</p>
                      )}
                      <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{tpl.body}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-3">
                    <button
                      data-testid={`copy-template-${tpl.id}`}
                      onClick={() => copyTemplateBody(tpl)}
                      className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`edit-template-${tpl.id}`}
                      onClick={() => { setEditingTemplate(tpl); setShowEditor(true); }}
                      className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`delete-template-${tpl.id}`}
                      onClick={() => deleteTemplate(tpl.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Facebook Groups */}
      <div className="border border-zinc-200 rounded-sm p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
          Gruppi Facebook & Marketplace
        </h2>
        <p className="text-sm text-zinc-500">
          Gestisci i gruppi Facebook e Marketplace da monitorare per annunci di privati.
        </p>

        {/* Add new group */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="fb-group-name-input"
            value={newFbName}
            onChange={(e) => setNewFbName(e.target.value)}
            placeholder="Nome gruppo (opzionale)"
            className="h-9 rounded-sm border-zinc-300 text-sm sm:w-48"
          />
          <Input
            data-testid="fb-group-url-input"
            value={newFbUrl}
            onChange={(e) => setNewFbUrl(e.target.value)}
            placeholder="https://www.facebook.com/groups/..."
            className="h-9 rounded-sm border-zinc-300 text-sm flex-1"
          />
          <Button
            data-testid="add-fb-group-btn"
            onClick={addFbGroup}
            className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 px-4 text-xs font-semibold shrink-0"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
          </Button>
        </div>

        {/* Groups list */}
        <div className="space-y-2">
          {fbGroups.length === 0 ? (
            <p className="text-sm text-zinc-400 py-2">Nessun gruppo configurato.</p>
          ) : (
            fbGroups.map(group => (
              <div
                key={group.id}
                data-testid={`fb-group-${group.id}`}
                className={`flex items-center justify-between p-3 border border-zinc-200 rounded-sm transition-all ${!group.active ? "opacity-50 bg-zinc-50" : "hover:shadow-sm"}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-800 truncate">{group.name}</div>
                    <a
                      href={group.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline truncate block"
                    >
                      {group.url}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <Switch
                    data-testid={`toggle-fb-group-${group.id}`}
                    checked={group.active}
                    onCheckedChange={() => toggleFbGroup(group.id)}
                    className="h-4 w-7"
                  />
                  <button
                    data-testid={`delete-fb-group-${group.id}`}
                    onClick={() => deleteFbGroup(group.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Facebook Cookie Management */}
      <div className="border border-zinc-200 rounded-sm p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
          Cookie Facebook
        </h2>
        <p className="text-sm text-zinc-500">
          I cookie servono per accedere ai gruppi Facebook. Scadono ogni ~2 settimane.
        </p>

        {/* Status */}
        {fbCookieStatus && (
          <div className={`flex items-center gap-3 p-3 rounded-sm border ${
            fbCookieStatus.is_valid
              ? "bg-emerald-50 border-emerald-200"
              : fbCookieStatus.configured
                ? "bg-amber-50 border-amber-200"
                : "bg-red-50 border-red-200"
          }`}>
            <div className={`w-3 h-3 rounded-full ${
              fbCookieStatus.is_valid ? "bg-emerald-500" : fbCookieStatus.configured ? "bg-amber-500" : "bg-red-500"
            }`} />
            <div>
              <div className={`text-sm font-semibold ${
                fbCookieStatus.is_valid ? "text-emerald-800" : fbCookieStatus.configured ? "text-amber-800" : "text-red-800"
              }`}>
                {fbCookieStatus.is_valid ? "Cookie validi" : fbCookieStatus.configured ? "Cookie configurati — verifica necessaria" : "Cookie non configurati"}
              </div>
              {fbCookieStatus.last_updated && (
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Ultimo aggiornamento: {new Date(fbCookieStatus.last_updated).toLocaleString("it-IT")}
                </div>
              )}
              {fbCookieStatus.last_verified && (
                <div className="text-[10px] text-zinc-500">
                  Ultima verifica: {new Date(fbCookieStatus.last_verified).toLocaleString("it-IT")}
                </div>
              )}
            </div>
            <Button
              data-testid="verify-fb-cookies-btn"
              onClick={async () => {
                setFbVerifying(true);
                try {
                  const res = await axios.post(`${API}/fb-cookies/verify`);
                  setFbCookieStatus(prev => ({ ...prev, ...res.data }));
                  toast[res.data.is_valid ? "success" : "error"](
                    res.data.is_valid ? "Cookie validi!" : "Cookie scaduti — aggiornali qui sotto"
                  );
                } catch { toast.error("Errore verifica"); }
                setFbVerifying(false);
              }}
              disabled={fbVerifying}
              variant="outline"
              className="ml-auto h-8 rounded-sm text-xs font-semibold shrink-0"
            >
              {fbVerifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Verifica ora"}
            </Button>
          </div>
        )}

        {/* Update form */}
        <div className="space-y-2 p-3 bg-zinc-50 rounded-sm border border-zinc-200">
          <div className="text-xs font-semibold text-zinc-600 mb-2">Aggiorna cookie (da Chrome → Impostazioni → Cookie → facebook.com)</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="fb-cookie-c-user"
              value={fbCookieForm.c_user}
              onChange={(e) => setFbCookieForm(prev => ({ ...prev, c_user: e.target.value }))}
              placeholder="c_user (es. 1136228355)"
              className="h-9 rounded-sm border-zinc-300 text-sm sm:flex-1"
            />
            <Input
              data-testid="fb-cookie-xs"
              value={fbCookieForm.xs}
              onChange={(e) => setFbCookieForm(prev => ({ ...prev, xs: e.target.value }))}
              placeholder="xs (stringa lunga)"
              className="h-9 rounded-sm border-zinc-300 text-sm sm:flex-[2]"
            />
            <Button
              data-testid="save-fb-cookies-btn"
              onClick={async () => {
                if (!fbCookieForm.c_user || !fbCookieForm.xs) {
                  toast.error("Inserisci entrambi i cookie");
                  return;
                }
                setFbSaving(true);
                try {
                  const res = await axios.post(`${API}/fb-cookies/update`, fbCookieForm);
                  setFbCookieStatus(prev => ({ ...prev, configured: true, is_valid: true, last_updated: res.data.last_updated }));
                  setFbCookieForm({ c_user: "", xs: "" });
                  toast.success("Cookie aggiornati!");
                } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                setFbSaving(false);
              }}
              disabled={fbSaving}
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 px-4 text-xs font-semibold shrink-0"
            >
              {fbSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Salva</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Schedule Info */}
      <div className="border border-zinc-200 rounded-sm p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
          Programmazione ricerca
        </h2>
        <div className="flex items-center gap-3 p-4 bg-zinc-50 rounded-sm">
          <div className="w-10 h-10 bg-zinc-900 rounded-sm flex items-center justify-center">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">Ricerca automatica ogni mattina</div>
            <div className="text-xs text-zinc-500">Ore 07:00 — Scraping reale da Immobiliare.it e Subito.it + altri portali attivi</div>
          </div>
        </div>
      </div>

      {/* Search Logs */}
      <div className="border border-zinc-200 rounded-sm p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
          Cronologia ricerche
        </h2>
        {searchLogs.length === 0 ? (
          <p className="text-sm text-zinc-400">Nessuna ricerca effettuata.</p>
        ) : (
          <div className="space-y-2">
            {searchLogs.map(log => (
              <div key={log.id} data-testid={`search-log-${log.id}`} className="p-3 bg-zinc-50 rounded-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${log.status === 'completed' ? 'bg-emerald-500' : log.status === 'running' ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`} />
                    <div>
                      <div className="text-sm font-medium text-zinc-700">{formatDate(log.started_at)}</div>
                      <div className="text-xs text-zinc-400">{log.portals_searched?.length || 0} portali</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-zinc-900 tabular-nums">{log.properties_found}</div>
                    <div className="text-xs text-zinc-400">annunci</div>
                  </div>
                </div>
                {/* Portal results breakdown */}
                {log.portal_results && Object.keys(log.portal_results).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1.5">
                    {Object.entries(log.portal_results).map(([portal, val]) => {
                      const count = typeof val === 'object' ? (val.privati || 0) + (val.agenzie || 0) : (val || 0);
                      return (
                        <span
                          key={portal}
                          className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                            count > 0 ? 'bg-emerald-100 text-emerald-700' :
                            count === 0 ? 'bg-amber-100 text-amber-700' :
                            'bg-zinc-100 text-zinc-400'
                          }`}
                        >
                          {portal}: {typeof val === 'object' ? `${val.privati || 0}P / ${val.agenzie || 0}A` : (count === -1 ? 'N/A' : count)}
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Errors */}
                {log.errors && log.errors.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-100">
                    {log.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-500">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Management */}
      <UserManagement />
    </div>
  );
}
